import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';

/**
 * FileUpload Component - S3-backed file upload with drag-and-drop
 *
 * @param {string} folder - S3 folder prefix (education, events, meeting-minutes, management-guide, news, registration)
 * @param {function} onUpload - Callback receiving the uploaded file URL
 * @param {string} currentUrl - Existing file URL to display (if any)
 * @param {string} label - Label for the upload field
 * @param {boolean} adminOnly - If true, uses /api/admin/upload endpoint; otherwise /api/upload
 * @param {number} maxSize - Max file size in bytes (default 50MB)
 * @param {string} accept - File accept attribute (default: all allowed types)
 * @param {function} onClear - Callback when user clears the file
 */
const FileUpload = ({
  folder = 'documents',
  onUpload,
  currentUrl = '',
  label = 'Upload File',
  adminOnly = false,
  maxSize = 50 * 1024 * 1024,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.txt,.csv,.rtf,.jpg,.jpeg,.png,.gif,.webp,.svg,.bmp,.mp4,.webm,.mov,.avi,.mkv',
  onClear,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [fileUrl, setFileUrl] = useState(currentUrl);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Extract filename from URL for display
  const getFileNameFromUrl = (url) => {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1] || 'file';
  };

  const validateFile = (file) => {
    if (!file) {
      return 'No file selected';
    }
    if (file.size > maxSize) {
      return `File too large. Maximum size: ${Math.floor(maxSize / 1024 / 1024)}MB`;
    }
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    const allowedExts = accept.split(',').map((e) => e.trim().toLowerCase());
    if (!allowedExts.includes(ext)) {
      return `File type not allowed. Allowed: ${accept}`;
    }
    return null;
  };

  const handleUpload = useCallback(
    async (file) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      setError('');
      setUploading(true);
      setUploadProgress(0);
      setFileName(file.name);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const endpoint = adminOnly ? '/api/admin/upload' : '/api/upload';

      try {
        const response = await axios.post(`${backendUrl}${endpoint}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(progress);
            }
          },
        });

        if (response.data.success) {
          setFileUrl(response.data.file_url);
          setFileName(response.data.filename || file.name);
          if (onUpload) {
            onUpload(response.data.file_url, response.data.filename);
          }
        }
      } catch (err) {
        const errorMsg =
          err.response?.data?.detail || err.response?.data?.message || 'Failed to upload file';
        setError(errorMsg);
      } finally {
        setUploading(false);
      }
    },
    [backendUrl, folder, adminOnly, onUpload, accept, maxSize]
  );

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleClear = () => {
    setFileUrl('');
    setFileName('');
    setError('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onClear) {
      onClear();
    }
    if (onUpload) {
      onUpload('', '');
    }
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>

      {/* Current file display */}
      {fileUrl && !uploading && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center gap-2 min-w-0">
            <svg
              className="h-5 w-5 flex-shrink-0 text-[#2c3e7d]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-sm text-[#2c3e7d] hover:underline"
              title={getFileNameFromUrl(fileUrl)}
            >
              {fileName || getFileNameFromUrl(fileUrl)}
            </a>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="ml-2 flex-shrink-0 text-gray-400 hover:text-red-600"
            title="Remove file"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Upload progress bar */}
      {uploading && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
            <span>Uploading {fileName}...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-[#2c3e7d] transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Drag-and-drop zone */}
      {!fileUrl && !uploading && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            isDragging
              ? 'border-[#2c3e7d] bg-blue-50'
              : 'border-gray-300 hover:border-[#2c3e7d] hover:bg-gray-50'
          }`}
        >
          <svg
            className="mx-auto h-10 w-10 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-600">
            <span className="font-medium text-[#2c3e7d]">Click to upload</span> or drag and drop
          </p>
          <p className="mt-1 text-xs text-gray-500">
            PDF, Word, Excel, PowerPoint, images, video (max {Math.floor(maxSize / 1024 / 1024)}MB)
          </p>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      {/* Error message */}
      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default FileUpload;
